<script>
	// libraries
	import { LayerCake, ScaledSvg, Html } from "layercake";
	import { scaleQuantize, scaleBand } from "d3-scale";

	// components
	import AxisX from "../components/AxisX.svelte";
	import AxisY from "../components/AxisY.svelte";
	import VerticalBars from "../components/VerticalBars.svelte";
	import SharedTooltip from "../components/SharedTooltip.svelte";
	import Labels from "../components/Labels.svelte";
	import Timeline from "../components/Timeline.svelte";
	import TimelineLabel from "../components/TimelineLabel.svelte";
	import BaselineLabel from "../components/BaselineLabel.svelte";

	// other
	import { frmtAnomaly, frmtMonthShort, getTextColor } from "../utils";
	import { reds, blues } from "../constants";
	import { selectedMode, selectedMonthColor, lastYearColor } from "../stores";

	// ///////////////////////////////////////////////////////////
	// properties
	// ///////////////////////////////////////////////////////////
	export let data;
	export let labels = [];
	export let ticks = [];
	export let mobileTicks = [];
	export let baselineLabelMain;
	export let baselineLabelSub = "";
	export let selectedMonth = "";
	export let marginLeft = 0;
	export let marginRight = 0;
	export let highlight = [];

	export let formatTick = (tick, i) => tick;

	export let xKey = "year";
	export let yKey = "anomaly";
	export let zKey = "anomaly";

	export let xDomain = [null, null];

	let max;
	$: {
		max = 0;
		data.forEach((d) => {
			max = Math.max(max, d[yKey]);
			max = Math.max(max, -d[yKey]);
		});
	}

	let hovered;
	// ///////////////////////////////////////////////////////////
	// constants and definitions
	// ///////////////////////////////////////////////////////////

	const opacity = 0.4;

	const bluesReverse = [...blues].reverse();

	const zScale = scaleQuantize();
	const zRange = bluesReverse.concat(reds);
	$: zDomain = [-max, max];

	$: zScaleAdditional = scaleQuantize().domain(zDomain).range(zRange);

	$: lastData = data.find((d) => d[xKey] === xDomain.at(-1));
	$: if ($selectedMode === "months") $selectedMonthColor = zScaleAdditional(lastData[zKey]);
	$: if ($selectedMode === "years") $lastYearColor = zScaleAdditional(lastData[zKey]);

	// only show tooltip if the data is not fake
	const isValid = (d) => (d && d.date ? d : undefined);
	$: hoveredValid = isValid(hovered);
</script>

<LayerCake
	ssr={true}
	percentRange={true}
	padding={{ top: 55, right: 15, bottom: 80, left: 50 }}
	x={xKey}
	y={yKey}
	z={zKey}
	yNice={true}
	xScale={scaleBand()}
	{zScale}
	{zDomain}
	{zRange}
	{data}
	custom={{ marginLeft: marginLeft, marginRight: marginRight }}
>
	<Html>
		{#if $selectedMode === "months"}
			<TimelineLabel label={selectedMonth} />
		{/if}
		<Timeline
			ticks={(tick, i) => ticks.includes(tick)}
			mobileTicks={(tick, i) => mobileTicks.includes(tick)}
			formatTick={(tick, i, isFirst, isLast) => formatTick(tick, i)}
			monthLabel={frmtMonthShort(selectedMonth)}
		/>
		<AxisY
			gridlines={true}
			baseline={false}
			ticksPositionX={"left"}
			ticksPositionY={"on-gridline"}
			xTick={0}
			ticks={[-10, -8, -6, -4, -2, 0, 2, 4, 6, 8, 10]}
			formatTick={(d, i, isLast) =>
				`${d !== 0 ? frmtAnomaly(d) : "<div style='font-size: 23px; color: #2a2a2a'>⌀</div>"} ${
					isLast ? " Abweichung" : ""
				}`}
		/>
		<BaselineLabel labelMain={baselineLabelMain} labelSub={baselineLabelSub} />
	</Html>

	<ScaledSvg>
		<VerticalBars
			hovered={hoveredValid}
			{opacity}
			highlight={(d, i) => (d.year >= highlight[0] && d.year <= highlight[1]) || i === data.length - 1}
		/>
	</ScaledSvg>

	<Html>
		<AxisX
			gridlines={false}
			baselineColor="#2a2a2a"
			baselineWidth={2}
			baseline={true}
			baselineOffsetX={20}
			ticks={[]}
		/>
		{#if !hoveredValid}
			<!-- label for latest value -->
			<Labels which={(d, i) => d[xKey] === xDomain.at(-1)} let:d let:yValue let:zResult let:invert>
				<div class="label-latest-container" class:invert>
					<div class="label label-latest" style:background={zResult} style:color={getTextColor(zResult)}>
						<div class="label-date">
							{@html d.month ? `${frmtMonthShort(d.month)} ${d.year}` : d.year}
						</div>
						<div class="label-value">
							{@html frmtAnomaly(yValue)}
						</div>
					</div>
					<div class="label-arrow" style:border-top-color={zResult} />
				</div>
			</Labels>
			<!-- label for highlighted values -->
			<Labels
				zIndex={-1}
				which={(d, i) => labels.includes(d.year)}
				let:d
				let:yValue
				let:zResult
				let:leftEdge
				let:invert
				hideAtTabletS={true}
			>
				<div class="label label-highlighted" style:color={zResult} class:left={leftEdge} class:invert>
					<div class="label-date">
						{@html d.month ? `${frmtMonthShort(d.month)} ${d.year}` : d.year}
					</div>
					<div class="label-value">
						{@html frmtAnomaly(yValue)}
					</div>
				</div>
			</Labels>
		{/if}
		<SharedTooltip {isValid} bind:d={hovered} let:d let:yValue let:zResult>
			<div class="tooltip">
				<div class="tooltip-date">
					{@html d.month ? `${frmtMonthShort(d.month)} ${d.year}` : d.year}
				</div>
				<div class="tooltip-value anomaly" style:background={zResult} style:color={getTextColor(zResult)}>
					{@html frmtAnomaly(yValue)}
				</div>
			</div>
		</SharedTooltip>
	</Html>
</LayerCake>

<style lang="scss">
	@import "../main.scss";

	.tooltip,
	.label {
		padding: 7px 4px;
		text-align: center;
		z-index: 1;
		position: relative;
		white-space: nowrap;
	}

	.label {
		transition: $color-transition;
		display: inline-block;
		transform: translate(-50%, -100%);

		&.invert {
			transform: translate(-50%, 0);
		}
	}

	.tooltip {
		display: flex;
		flex-direction: column;
		align-items: center;
	}

	.label-latest-container {
		display: inline-flex;
		flex-direction: column;
		transform: translate(-50%, -100%);

		.label-latest {
			padding: 7px 10px;
			width: max-content;
			border-radius: 5px;
			z-index: 2;
			color: white;
			box-shadow: 0px 5px 10px rgba(0, 0, 0, 0.15);
			z-index: 1;
			position: relative;
			transform: none;
		}

		.label-arrow {
			width: 0;
			height: 0;
			border-left: 6px solid transparent;
			border-right: 6px solid transparent;
			border-top: 6px solid;
			transform: translateX(-50%);
			left: 50%;
			position: relative;
			z-index: 1;
		}

		&.invert {
			flex-direction: column-reverse;
			transform: translate(-50%, 0);

			.label-arrow {
				transform: translate(-50%) scale(-1);
			}
		}
	}

	// .invert {

	// }

	.label-highlighted.left {
		transform: translate(25%, 0);
	}

	// <= small  tablets
	@media (max-width: $breakpoint-tablet-s) {
		.label-highlighted {
			display: none;
		}

		.label-latest {
			transform: translate(-35%, 0) !important;
		}
	}
	// <= large tablets
	@media (max-width: $breakpoint-tablet-l) {
		// .label-latest {
		// 	transform: translate(-30%, 0);
		// }
	}

	.tooltip-value,
	.label-value {
		font-weight: $medium;
		margin-top: 1px;
	}
</style>
